import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime, fphone, fcfa } from "@/lib/format";
import { methodLabel, purposeLabel, roleLabel } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Loader, PageTitle, StatusBadge } from "@/components/common";
import { Contribution } from "@/components/member/Contribution";
import { ActivityTab, DataTab, InvitationsTab, SecurityTab, SponsorTab } from "@/components/member/MeTabs";
import { PushCard } from "@/components/member/PushCard";
import { NotificationPrefs } from "@/components/member/NotifPrefs";
import { MyAgreements } from "@/components/member/LeadTools";
import { MyRequests } from "@/components/member/MyRequests";
import { ProfileMedia } from "@/components/member/ProfileMedia";

const TABS = [["profil", "Profil"], ["cotisation", "Cotisation"], ["activite", "Activité"], ["paiements", "Paiements"], ["notifications", "Notifications"], ["conventions", "Conventions"], ["demandes", "Mes demandes"], ["invitations", "Invitations"],
  ["parrainage", "Parrainage"], ["appareils", "Appareils"], ["securite", "Sécurité"], ["donnees", "Mes données"]];
const VIS = [["all", "Tous les membres"], ["contacts", "Mes contacts"], ["nobody", "Personne"]];

function ProfileTab() {
  const { me, refresh } = useAuth();
  const { settings } = useConfig();
  const p = me.profile || {};
  const [f, setF] = useState({ name: me.user.name, email: me.user.email || "", company: p.company || "", city: p.city || "", sectors: p.sectors || [],
    product_categories: p.product_categories || [], sourcing_countries: p.sourcing_countries || [], languages: p.languages || [],
    show_operations: p.show_operations !== false, language: me.user.preferences?.language || "fr", notifications: me.user.preferences?.notifications !== false,
    brings: p.brings || "", seeks: p.seeks || "", bio: p.bio || "", headline: p.headline || "", contact_visibility: p.contact_visibility || { phone: "contacts", email: "contacts", whatsapp: "contacts" } });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSector = (s) => setF({ ...f, sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s].slice(0, 5) });
  const toggleIn = (k, s, max) => setF({ ...f, [k]: f[k].includes(s) ? f[k].filter((x) => x !== s) : [...f[k], s].slice(0, max) });
  const save = async () => {
    setBusy(true);
    try {
      const { language, notifications, ...rest } = f;
      await api.patch("/me/profile", { ...rest, email: f.email || null, preferences: { language, notifications } });
      await refresh();
      toast.success("Profil enregistré");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const input = (k, label, props = {}) => (
    <label className="block"><span className="mr-label">{label}</span><input data-testid={`profile-${k}-input`} className="mr-input" value={f[k]} onChange={set(k)} {...props} /></label>
  );
  return (
    <div className="space-y-5">
      <ProfileMedia />
      <div className="mr-card space-y-4">
        {input("name", "Nom et prénom")}
        {input("headline", "Titre du profil", { placeholder: "Ex. Directrice générale · Import de matériaux", maxLength: 120 })}
        {input("email", "E-mail", { type: "email" })}
        {input("company", "Entreprise")}
        <label className="block"><span className="mr-label">Commune / ville</span>
          <select data-testid="profile-city-select" className="mr-input" value={f.city} onChange={set("city")}>
            <option value="">Choisir…</option>
            {(settings["catalog.cities"] || []).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <div><span className="mr-label">Secteurs (5 maximum)</span>
          <div className="flex flex-wrap gap-2">
            {(settings["catalog.sectors"] || []).map((s) => (
              <button type="button" key={s} onClick={() => toggleSector(s)} data-testid={`profile-sector-${s}`}
                className={`px-3 h-9 rounded-full border text-xs ${f.sectors.includes(s) ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>{s}</button>
            ))}
          </div>
        </div>
        {[["product_categories", "Catégories de produits", "catalog.product_categories", 8], ["sourcing_countries", "Pays d'approvisionnement", "catalog.countries", 8], ["languages", "Langues", "catalog.languages", 6]].map(([k, l, key, max]) => (
          <div key={k}><span className="mr-label">{l}</span><div className="flex flex-wrap gap-2">{(settings[key] || []).map((s) => (
            <button type="button" key={s} onClick={() => toggleIn(k, s, max)} data-testid={`profile-${k}-${s}`}
              className={`px-3 h-9 rounded-full border text-xs ${f[k].includes(s) ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>{s}</button>))}</div></div>
        ))}
        {input("brings", "J'apporte")}
        {input("seeks", "Je cherche")}
        <label className="block"><span className="mr-label">Présentation</span><textarea data-testid="profile-bio-input" className="mr-input min-h-[90px]" value={f.bio} onChange={set("bio")} /></label>
      </div>
      <div className="mr-card space-y-4" data-testid="profile-visibility-card">
        <p className="font-medium">Qui peut voir mes coordonnées ?</p>
        {[["phone", "Téléphone"], ["whatsapp", "WhatsApp"], ["email", "E-mail"]].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-3">
            <span className="text-sm text-mr-text2">{l}</span>
            <select data-testid={`visibility-${k}-select`} className="mr-input w-44" value={f.contact_visibility[k]} onChange={(e) => setF({ ...f, contact_visibility: { ...f.contact_visibility, [k]: e.target.value } })}>
              {VIS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="mr-card space-y-4">
        <label className="flex items-center justify-between gap-3"><span className="text-sm text-mr-text2">Afficher mes opérations sur mon profil</span>
          <input type="checkbox" data-testid="profile-show-operations" checked={f.show_operations} onChange={(e) => setF({ ...f, show_operations: e.target.checked })} /></label>
        <label className="flex items-center justify-between gap-3"><span className="text-sm text-mr-text2">Notifications par e-mail et sur l'appareil</span>
          <input type="checkbox" data-testid="profile-notifications" checked={f.notifications} onChange={(e) => setF({ ...f, notifications: e.target.checked })} /></label>
        <label className="flex items-center justify-between gap-3"><span className="text-sm text-mr-text2">Langue de l'application</span>
          <select data-testid="profile-language-select" className="mr-input w-44" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}><option value="fr">Français</option></select></label>
      </div>
      <button data-testid="profile-save-button" className="mr-btn-primary w-full" onClick={save} disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
    </div>
  );
}

function MembershipTab() {
  return <Contribution />;
}


function PaymentsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/me/payments").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  if (!items) return <Loader />;
  if (!items.length) return <p className="text-sm text-mr-text2">Aucun paiement enregistré.</p>;
  return (
    <ul className="space-y-2">
      {items.map((p) => (
        <li key={p.id} className="mr-card p-4" data-testid="my-payment-row">
          <div className="flex justify-between gap-3">
            <p className="font-medium text-sm">{purposeLabel(p.purpose)}</p>
            <StatusBadge status={p.status} />
          </div>
          <div className="flex justify-between text-sm text-mr-text2 mt-1 tabular">
            <span>{methodLabel(p.method)} · {fdatetime(p.created_at)}</span>
            <span className="text-mr-text">{fcfa(p.amount)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DevicesTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/auth/sessions").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const revoke = async (id) => {
    await api.delete(`/auth/sessions/${id}`);
    toast.success("Appareil déconnecté");
    load();
  };
  const install = async () => {
    const prompt = window.__mrInstallPrompt;
    if (prompt) await prompt.prompt();
    else toast("Sur Android : menu du navigateur → « Ajouter à l'écran d'accueil ».");
  };
  if (!items) return <Loader />;
  return (
    <div className="space-y-3">
      <button data-testid="install-app-button" className="mr-btn-secondary w-full" onClick={install}><Smartphone size={16} /> Installer l'application</button>
      <PushCard />
      {items.map((s) => (
        <div key={s.id} className="mr-card p-4 flex items-center justify-between gap-3" data-testid="session-row">
          <div className="text-sm">
            <p className="font-medium">{s.device} {s.current && <span className="text-mr-accent text-xs ml-1">(cet appareil)</span>}</p>
            <p className="text-mr-muted tabular">Dernière activité : {fdatetime(s.last_seen_at)}</p>
          </div>
          {!s.current && <button data-testid="session-revoke-button" className="mr-btn-danger px-3" onClick={() => revoke(s.id)}>Déconnecter</button>}
        </div>
      ))}
    </div>
  );
}


const PANELS = { profil: ProfileTab, cotisation: MembershipTab, activite: ActivityTab, paiements: PaymentsTab, notifications: NotificationPrefs, conventions: MyAgreements, demandes: MyRequests, invitations: InvitationsTab,
  parrainage: SponsorTab, appareils: DevicesTab, securite: SecurityTab, donnees: DataTab };

export default function Me() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profil";
  const Panel = PANELS[tab] || ProfileTab;
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Moi" title={me.user.name}>
        <StatusBadge status={me.user.status} testId="me-status-badge" />
      </PageTitle>
      <p className="text-sm text-mr-muted -mt-4 mb-6 tabular">{fphone(me.user.phone)} · {roleLabel(me.user.global_role)}</p>
      <div className="flex gap-1 overflow-x-auto -mx-4 px-4 mb-6 border-b border-mr-border">
        {TABS.map(([id, l]) => (
          <button key={id} data-testid={`me-tab-${id}`} onClick={() => setParams({ tab: id })}
            className={`shrink-0 px-3 min-h-[44px] text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted hover:text-mr-text"}`}>{l}</button>
        ))}
      </div>
      <Panel />
      <button data-testid="logout-button" className="mr-btn-ghost w-full mt-8" onClick={async () => { await logout(); nav("/"); }}><LogOut size={16} /> Se déconnecter</button>
    </div>
  );
}
